// Learn Admin Section - Admin interface for managing learn content
// Follows the Section, Component & Manager System architecture

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { useAuth } from '../../../../contexts/AuthContext';
import { useManager } from '../../../../core';
import { LearnManager } from '../../managers/learn-manager';
import { LearnPageHeader, LearnLoading, AdminHomeTab } from '../../components';
import { AdminQAPage } from './AdminQAPage';
import { TriviaAdminPage } from './TriviaAdminPage';
import { TutorialVideosLayout } from '../tutorial-videos/TutorialVideosLayout';
import { TutorialVideosContent } from '../tutorial-videos/TutorialVideosContent';
import { TutorialVideosGrid } from '../tutorial-videos/TutorialVideosGrid';
import { RevoltButton } from '../../../../components';
import { CategoryDeleteConfirmModal } from '../../../workspace/components/CategoryDeleteConfirmModal';
import { VideoDeleteConfirmModal } from '../../../workspace/components/VideoDeleteConfirmModal';
import { VideoModal } from './VideoModal';
import { CategoryModal } from './CategoryModal';
import '../../LearnShared.css';
import './AdminContentTabs.css';

export function LearnAdminSection() {
  const { user, loading: authLoading } = useAuth();
  const { manager: learnManager, isInitialized } = useManager(LearnManager);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentTab = searchParams.get('tab') || 'tutorial-videos';

  // Admin tutorial videos specific state
  const adminVideoTab = searchParams.get('adminTab') || '';

  // Modal state
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoModalMode, setVideoModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedVideo, setSelectedVideo] = useState(null);

  // Category modal state
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Category delete confirmation modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Video delete confirmation modal state
  const [isVideoDeleteModalOpen, setIsVideoDeleteModalOpen] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);

  // Categories are now loaded by the manager

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Categories are loaded by the LearnManager

  // Handle viewing video in learn page
  const handleViewInLearnPage = (videoData) => {
    if (videoData && videoData.id && videoData.categoryId) {
      navigate(`/learn/tutorial-videos?video=${videoData.id}&tab=${videoData.categoryId}`);
    }
  };

  // Handle editing a video (memoized for useEffect dependency)
  const handleEditVideo = useCallback(async (videoId) => {
    try {
      // Find the video in the manager's data
      const video = learnManager.tutorialVideos.find(v => v.id === videoId);
      if (video) {
        setVideoModalMode('edit');
        setSelectedVideo(video);
        setIsVideoModalOpen(true);
      } else {
        console.error('Video not found:', videoId);
        alert('Video not found. Please refresh the page and try again.');
      }
    } catch (error) {
      console.error('Error loading video for edit:', error);
      alert('Failed to load video data. Please try again.');
    }
  }, [learnManager.tutorialVideos]);

  // Handle navigation payload for auto-opening edit modal
  useEffect(() => {
    if (isInitialized && location.state) {
      const payload = location.state;
      if (payload.action === 'editVideo' && payload.videoId) {
        // Auto-open edit modal for the specified video
        handleEditVideo(payload.videoId);

        // Set the correct tab and category if provided
        if (payload.categoryId) {
          setSearchParams({
            tab: 'tutorial-videos',
            adminTab: payload.categoryId
          });
        } else {
          setSearchParams({ tab: 'tutorial-videos' });
        }
      }
    }
  }, [isInitialized, location.state, handleEditVideo, setSearchParams]);

  // Get categories from manager (defined early for useEffect dependencies)
  const categories = useMemo(() => learnManager.categories || [], [learnManager.categories]);

  // Handle invalid category IDs in URL (redirect to 'all')
  useEffect(() => {
    if (isInitialized && adminVideoTab && adminVideoTab !== 'all') {
      const categoryExists = categories.some(cat => cat.id === adminVideoTab);
      if (!categoryExists) {
        // Category doesn't exist, redirect to 'all'
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('adminTab', 'all');
        setSearchParams(newSearchParams);
      }
    }
  }, [isInitialized, adminVideoTab, categories, searchParams, setSearchParams]);

  // Check if manager is initialized
  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--learn">
        <LearnLoading
          title="Initializing Admin Panel"
          subtitle="Setting up content management tools..."
        />
      </MainLayout>
    );
  }

  // Set active category: 'all' for all videos (default), or specific category ID
  const activeCategoryId = adminVideoTab || 'all';

  // Filter videos by selected category
  const filteredVideos = learnManager.getVideosByCategory(activeCategoryId);

  // Show loading while checking auth
  if (authLoading) {
    return (
      <MainLayout contentClassName="main-content--learn">
        <LearnLoading
          title="Loading Admin Panel"
          subtitle="Verifying access permissions..."
        />
      </MainLayout>
    );
  }

  // Check admin access - only show admin panel for admins
  if (!user || user.role !== 'admin') {
    return (
      <MainLayout contentClassName="main-content--learn">
        <div className="learn-container">
          <div className="access-denied">
            <i className="fa-solid fa-shield-alt" style={{ fontSize: '3rem', color: '#ff4444', marginBottom: '20px' }}></i>
            <h1>Access Denied</h1>
            <p>This admin section is only available to administrators.</p>
            <p>Please contact support if you believe this is an error.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const tabs = [
    { id: 'home', label: 'Home', icon: 'fa-home' },
    { id: 'tutorial-videos', label: 'Tutorial Videos', icon: 'fa-video' },
    { id: 'questions-answers', label: 'Questions & Answers', icon: 'fa-question-circle' },
    { id: 'trivia', label: 'Trivia', icon: 'fa-brain' }
  ];

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'home':
        return <AdminHomeTab />;

      case 'tutorial-videos':
        // Category info is now dynamic based on selected category

        return (
          <div className="admin-tab-content">
            <div className="admin-section-header admin-section-header--with-button">
              <div className="admin-section-header-left">
                <h2>Tutorial Videos Management</h2>
                <p>Manage tutorial videos, categories, and content.</p>
              </div>
              <div className="admin-section-header-right">
                <RevoltButton
                  variant="primary"
                  size="medium"
                  onClick={handleCreateVideo}
                >
                  <i className="fa-solid fa-plus"></i>
                  Create Tutorial Video
                </RevoltButton>
              </div>
            </div>

            <TutorialVideosLayout
              sidebar={
                <div className="admin-sidebar-categories">
                  <h4>Categories</h4>
                  <div className="categories-list">
                    {categories.length === 0 ? (
                      <div className="categories-empty">
                        <span>No categories yet</span>
                      </div>
                    ) : (
                      categories.map((category) => (
                        <div
                          key={category.id}
                          className={`category-item ${draggedItem?.id === category.id ? 'category-item--dragging' : ''} ${dragOverItem?.id === category.id ? 'category-item--drag-over' : ''}`}
                          draggable
                          onDragStart={(e) => handleDragStart(e, category)}
                          onDragOver={(e) => handleDragOver(e, category)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, category)}
                        >
                          <div className="category-drag-handle">
                            <i className="fa-solid fa-grip-vertical"></i>
                          </div>
                          <span className="category-name">{category.name}</span>
                          <div className="category-actions">
                            <button
                              className="category-action-btn"
                              onClick={() => handleEditCategory(category)}
                              title="Edit category"
                            >
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button
                              className="category-action-btn category-action-btn--delete"
                              onClick={() => handleDeleteCategory(category)}
                              title="Delete category"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="add-category-section">
                    <RevoltButton
                      variant="outline"
                      size="small"
                      onClick={handleCreateCategory}
                      className="add-category-btn"
                    >
                      <i className="fa-solid fa-plus"></i>
                      Add Category
                    </RevoltButton>
                  </div>
                </div>
              }
              content={
                <TutorialVideosContent>
                  {/* Category Tabs */}
                  <div className="category-tabs-section">
                    <div className="category-tabs">
                      {/* All Videos Tab */}
                      <button
                        className={`category-tab ${activeCategoryId === 'all' ? 'active' : ''}`}
                        onClick={() => {
                          const newSearchParams = new URLSearchParams(searchParams);
                          newSearchParams.set('adminTab', 'all');
                          setSearchParams(newSearchParams);
                        }}
                      >
                        <span className="category-tab-name">All Videos</span>
                        <span className="category-tab-count">
                          {learnManager.getVideosByCategory('all').length}
                        </span>
                      </button>

                      {categories.map((category) => (
                        <button
                          key={category.id}
                          className={`category-tab ${activeCategoryId === category.id ? 'active' : ''}`}
                          onClick={() => {
                            const newSearchParams = new URLSearchParams(searchParams);
                            newSearchParams.set('adminTab', category.id);
                            setSearchParams(newSearchParams);
                          }}
                        >
                          <span className="category-tab-name">{category.name}</span>
                          <span className="category-tab-count">
                            {learnManager.getVideosByCategory(category.id).length}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Category Header */}
                  <div className="tutorial-videos-header">
                    <div className="category-title-section">
                      <div className="category-title-left">
                        <h2 className="category-title">
                          {activeCategoryId === 'all'
                            ? 'All Videos'
                            : categories.find(cat => cat.id === activeCategoryId)?.name || 'All Videos'
                          }
                        </h2>
                        <span className="category-count-badge">{filteredVideos.length}</span>
                      </div>
                    </div>
                    <p className="category-description">
                      {activeCategoryId === 'all'
                        ? 'Manage tutorial videos across all categories'
                        : categories.find(cat => cat.id === activeCategoryId)?.name
                          ? `Videos in the ${categories.find(cat => cat.id === activeCategoryId)?.name} category`
                          : 'Manage tutorial videos across all categories'
                      }
                    </p>
                  </div>

                  {filteredVideos.length === 0 ? (
                    <div className="learn-empty-state">
                      <i className="fa-solid fa-video-slash"></i>
                      <h3>No tutorial videos found</h3>
                      <p>Try adjusting your search terms or check back later for new content.</p>
                    </div>
                  ) : (
                    <TutorialVideosGrid
                      videos={filteredVideos}
                      currentCategory={adminVideoTab}
                      onVideoClick={handleEditVideo}
                      onVideoDelete={handleDeleteVideo}
                      isAdmin={true}
                    />
                  )}
                </TutorialVideosContent>
              }
            />
          </div>
        );

      case 'questions-answers':
        return <AdminQAPage />;

      case 'trivia':
        return <TriviaAdminPage />;

      default:
        return (
          <div className="admin-tab-content">
            <div className="admin-placeholder">
              <h3>Unknown Tab</h3>
              <p>Please select a valid admin section.</p>
            </div>
          </div>
        );
    }
  };

  // Video modal handlers
  const handleCreateVideo = () => {
    setVideoModalMode('create');
    setSelectedVideo(null);
    setIsVideoModalOpen(true);
  };

  const handleVideoModalSubmit = async (videoData) => {
    try {
      if (videoModalMode === 'create') {
        await learnManager.createTutorialVideo(videoData);
      } else {
        await learnManager.updateTutorialVideo(videoData.id, videoData);
      }
    } catch (error) {
      console.error('Error saving video:', error);
      throw error; // Re-throw to let modal handle error state
    }
  };

  const handleDeleteVideo = (video) => {
    setVideoToDelete(video);
    setIsVideoDeleteModalOpen(true);
  };

  const handleConfirmVideoDelete = async () => {
    if (!videoToDelete) return;

    setIsDeletingVideo(true);
    try {
      await learnManager.deleteTutorialVideo(videoToDelete.id);

      // Reload content to update videos
      await learnManager.loadContent();
      setIsVideoDeleteModalOpen(false);
      setVideoToDelete(null);

    } catch (error) {
      console.error('Error deleting video:', error);
      alert('Failed to delete video. Please try again.');
      setIsVideoDeleteModalOpen(false);
      setVideoToDelete(null);
    } finally {
      setIsDeletingVideo(false);
    }
  };

  // Categories are loaded by the LearnManager

  // Drag and drop handlers
  const handleDragStart = (e, category) => {
    setDraggedItem(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, category) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverItem(category);
  };

  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  const handleDrop = async (e, targetCategory) => {
    e.preventDefault();
    setDragOverItem(null);

    if (!draggedItem || draggedItem.id === targetCategory.id) {
      setDraggedItem(null);
      return;
    }

    // Find the new order
    const draggedIndex = categories.findIndex(cat => cat.id === draggedItem.id);
    const targetIndex = categories.findIndex(cat => cat.id === targetCategory.id);

    const newCategories = [...categories];
    const [removed] = newCategories.splice(draggedIndex, 1);
    newCategories.splice(targetIndex, 0, removed);

    // Manager will reload content after API call

    // Persist the new order to backend
    try {
      const categoryIds = newCategories.map(cat => cat.id);
      const response = await fetch('/api/learn/admin/categories/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ categoryIds }),
      });

      const data = await response.json();
      if (data.success) {
        // Reload content to get updated category order
        await learnManager.loadContent();
      } else {
        console.error('Failed to reorder categories:', data.error);
        // Reload categories to revert on error
        await learnManager.loadContent();
      }
    } catch (error) {
      console.error('Error reordering categories:', error);
      // Reload categories to revert on error
      await learnManager.loadContent();
    }

    setDraggedItem(null);
  };

  // Category modal handlers
  const handleCreateCategory = () => {
    setCategoryModalMode('create');
    setSelectedCategory(null);
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setCategoryModalMode('edit');
    setSelectedCategory(category);
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategory = (category) => {
    setDeleteItem({
      type: 'category',
      name: category.name,
      id: category.id
    });
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem || deleteItem.type !== 'category') return;

    setIsDeleting(true);
    try {
      const response = await fetch(`/api/learn/admin/categories/${deleteItem.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete category');
      }

      // Reload content to update categories
      await learnManager.loadContent();
      setIsDeleteModalOpen(false);
      setDeleteItem(null);

    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category: ' + error.message);
      setIsDeleteModalOpen(false);
      setDeleteItem(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCategoryModalSubmit = async (categoryData) => {
    try {
      let response;

      if (categoryModalMode === 'create') {
        // Create new category
        response = await fetch('/api/learn/admin/categories', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(categoryData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create category');
        }

        // Reload content to update categories
        await learnManager.loadContent();

      } else {
        // Update existing category
        response = await fetch(`/api/learn/admin/categories/${categoryData.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(categoryData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update category');
        }

        // Reload content to update categories
        await learnManager.loadContent();
      }

    } catch (error) {
      console.error('Error saving category:', error);
      throw error; // Re-throw to let modal handle error state
    }
  };

  return (
    <MainLayout contentClassName="main-content--learn">
      {/* Fixed Learn Page Header */}
      <LearnPageHeader />

      <div className="learn-container">
        <div className="admin-tabs-container">
          {/* Tab Navigation */}
          <div className="admin-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`admin-tab-button ${currentTab === tab.id ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
              >
                <i className={`fa-solid ${tab.icon}`}></i>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="admin-tabs-content">
            {renderTabContent()}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        mode={videoModalMode}
        initialData={selectedVideo}
        defaultCategoryId={activeCategoryId}
        onSubmit={handleVideoModalSubmit}
        onViewInLearnPage={handleViewInLearnPage}
      />

      {/* Category Modal */}
      <CategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        mode={categoryModalMode}
        initialData={selectedCategory}
        onSubmit={handleCategoryModalSubmit}
      />

      {/* Category Delete Confirmation Modal */}
      <CategoryDeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setIsDeleteModalOpen(false);
            setDeleteItem(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        item={deleteItem}
        isLoading={isDeleting}
      />

      {/* Video Delete Confirmation Modal */}
      <VideoDeleteConfirmModal
        isOpen={isVideoDeleteModalOpen}
        onClose={() => {
          if (!isDeletingVideo) {
            setIsVideoDeleteModalOpen(false);
            setVideoToDelete(null);
          }
        }}
        onConfirm={handleConfirmVideoDelete}
        video={videoToDelete}
        isLoading={isDeletingVideo}
      />
    </MainLayout>
  );
}